/**
 * Interest and reward credits from credit_history, derived client-side by
 * filtering the same paginated feed refunds.ts already reads independently
 * (matching that established per-type-fetch precedent rather than sharing
 * one cache entry across all three credit types).
 */
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { Interest, Reward } from 'models/credit';
import type { GetCreditHistoryResponse } from 'models/reqres';
import config from 'utils/config';

import { removeEmpty } from './utils';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

const CREDIT_HISTORY_PAGE_LIMIT = 1000;

export const creditHistoryDerivedKeys = {
    all: ['creditHistoryDerived'] as const,
    user: (userId: string) => [...creditHistoryDerivedKeys.all, userId] as const,
};

export interface CreditHistoryDerived {
    interestList: Interest[];
    rewardsList: Reward[];
}

async function fetchCreditHistoryDerived(userId: string, signal?: AbortSignal): Promise<CreditHistoryDerived> {
    const interestList: Interest[] = [];
    const rewardsList: Reward[] = [];
    let cursor: string | undefined;
    do {
        const response = await api.get<GetCreditHistoryResponse>(`/users/${userId}/credit_history`, {
            params: removeEmpty({ limit: CREDIT_HISTORY_PAGE_LIMIT, cursor }),
            signal,
        });
        for (const c of response.data.credits ?? []) {
            const mapped = {
                amountCents: c.amount_cents,
                createdAt: c.created_at,
                reason: c.reason,
                status: c.status,
                type: c.type,
            };
            if (c.type === 'interest') {
                interestList.push(mapped);
            }
            if (c.type === 'incentive' && c.reason.match(/(Volume|Liquidity) Incentive for .+/)) {
                const rewardType = c.reason.startsWith('Volume') ? 'volume_incentive' : 'liquidity_incentive';
                rewardsList.push({ ...mapped, type: rewardType });
            }
        }
        cursor = response.data.cursor || undefined;
    } while (cursor);
    return { interestList, rewardsList };
}

export function useCreditHistoryDerivedQuery() {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        queryKey: creditHistoryDerivedKeys.user(userId ?? ''),
        queryFn: ({ signal }) => fetchCreditHistoryDerived(userId!, signal),
        enabled: !!userId && isLoggedInMember,
        // Credits are effectively immutable once granted — same rationale as refunds.ts.
        staleTime: Infinity,
    });
}

export function useInterestList(): Interest[] {
    return useCreditHistoryDerivedQuery().data?.interestList ?? [];
}

export function useRewardsList(): Reward[] {
    return useCreditHistoryDerivedQuery().data?.rewardsList ?? [];
}

export function useCreditHistoryLoaded(): boolean {
    const query = useCreditHistoryDerivedQuery();
    return query.isFetched || query.isError;
}

export function useCreditHistoryLoading(): boolean {
    return useCreditHistoryDerivedQuery().isPending;
}

export function useCurrentYearInterestAccrued(): number {
    const interestList = useInterestList();
    const currentYear = DateTime.now().year;
    return interestList.reduce((acc, interest) => {
        const isCurrentYear = DateTime.fromISO(interest.createdAt).year === currentYear;
        return isCurrentYear ? acc + interest.amountCents : acc;
    }, 0);
}

export function useCurrentMonthRewardsAccrued(): number {
    const rewardsList = useRewardsList();
    const now = DateTime.now();
    return rewardsList.reduce((acc, reward) => {
        const rewardDate = DateTime.fromISO(reward.createdAt);
        const isCurrentMonth = rewardDate.month === now.month && rewardDate.year === now.year;
        return isCurrentMonth ? acc + reward.amountCents : acc;
    }, 0);
}

export function useLifetimeRewardsAccrued(): number {
    return useRewardsList().reduce((acc, reward) => acc + reward.amountCents, 0);
}
